import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import AndroidRoundedIcon from "@mui/icons-material/AndroidRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import SettingsSuggestRoundedIcon from "@mui/icons-material/SettingsSuggestRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import ArchitectureRoundedIcon from "@mui/icons-material/ArchitectureRounded";
import FlexibleStatsRow from "../../../components/common/FlexibleStatsRow";
import ScrollableTableContainer from "../../../components/common/ScrollableTableContainer";
import { whiteLabelApi } from "../../../redux/admin/whiteLabelApi";
import { toast } from "react-hot-toast";

const STAT_QUERY_PARAMS = Object.freeze({
  page: 1,
  limit: 100,
  sortBy: "createdAt",
  sortOrder: "desc",
});

const ACTION_GROUPS = Object.freeze([
  {
    key: "generate-assets",
    title: "Generate Assets",
    subtitle: "Prepare configuration and branding packages for build tooling once backend generation endpoints are added.",
    icon: <SettingsSuggestRoundedIcon fontSize="small" />,
    actions: [
      { key: "generate-config", label: "Generate Config" },
      { key: "generate-branding-assets", label: "Generate Branding Assets" },
    ],
    helper: "Generation status",
    statusLabel: "Backend Not Available",
  },
  {
    key: "android-build",
    title: "Android Build",
    subtitle: "Expose APK and AAB triggers only after the build orchestration backend is implemented.",
    icon: <AndroidRoundedIcon fontSize="small" />,
    actions: [
      { key: "build-apk", label: "Build APK" },
      { key: "build-aab", label: "Build AAB" },
    ],
    helper: "Current status",
    statusLabel: "Backend Not Available",
  },
  {
    key: "website-build",
    title: "Website Build",
    subtitle: "Generate website delivery packages once packaging support exists on the backend.",
    icon: <Inventory2RoundedIcon fontSize="small" />,
    actions: [{ key: "generate-website-package", label: "Generate Website Package" }],
    helper: "Package status",
    statusLabel: "Backend Not Available",
  },
  {
    key: "downloads",
    title: "Downloads",
    subtitle: "Downloads stay disabled until build artifacts are produced and indexed by the backend.",
    icon: <DownloadRoundedIcon fontSize="small" />,
    actions: [
      { key: "download-apk", label: "APK" },
      { key: "download-aab", label: "AAB" },
      { key: "download-website-package", label: "Website Package" },
    ],
    helper: "Artifact availability",
    statusLabel: "Backend Not Available",
  },
]);

function BuildPanel({ title, subtitle, icon, actions, helper, statusLabel }) {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.88)}`,
        bgcolor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.background.paper, 0.92)
            : alpha(theme.palette.common.white, 0.96),
        boxShadow:
          theme.palette.mode === "dark"
            ? `0 18px 40px ${alpha("#020617", 0.24)}`
            : `0 18px 40px ${alpha("#0f172a", 0.05)}`,
      }}
    >
      <Stack spacing={2} sx={{ p: { xs: 2, md: 2.5 } }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2.5,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {subtitle}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {actions.map((action) => (
            <Button
              key={action.key}
              variant="outlined"
              disabled
              sx={{ minWidth: 148, justifyContent: "flex-start" }}
            >
              {action.label}
            </Button>
          ))}
        </Stack>

        <Divider />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {helper}
          </Typography>
          <Chip
            label={statusLabel}
            variant="outlined"
            color="warning"
            sx={{ fontWeight: 800 }}
          />
        </Stack>
      </Stack>
    </Paper>
  );
}

function getErrorMessage(error, fallbackMessage) {
  return (
    error?.data?.msg ||
    error?.data?.message ||
    error?.message ||
    fallbackMessage
  );
}

function formatDateTime(value) {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

async function mapWithConcurrency(items, concurrency, mapper) {
  const results = new Array(items.length);
  let index = 0;

  const workerCount = Math.max(1, Math.min(concurrency, items.length || 1));

  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      while (index < items.length) {
        const currentIndex = index;
        index += 1;
        results[currentIndex] = await mapper(items[currentIndex], currentIndex);
      }
    })
  );

  return results;
}

export default function WhiteLabelBuildCenterPage() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [overview, setOverview] = useState({
    totalCompanies: 0,
    androidApps: 0,
    websites: 0,
    lastBuildAt: null,
    pendingBuilds: 0,
    companies: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let isMounted = true;

    const executeQuery = async (endpoint, arg) => {
      const request = dispatch(
        endpoint.initiate(arg, {
          forceRefetch: true,
        })
      );

      try {
        return await request.unwrap();
      } finally {
        request.unsubscribe();
      }
    };

    const loadOverview = async () => {
      setIsLoading(true);
      setErrorMessage("");

      try {
        const firstPage = await executeQuery(
          whiteLabelApi.endpoints.getWhiteLabelCompanies,
          STAT_QUERY_PARAMS
        );

        const totalPages = Math.max(
          1,
          Number(firstPage?.pagination?.totalPages || 1)
        );

        const remainingPages = [];
        for (let page = 2; page <= totalPages; page += 1) {
          remainingPages.push(
            executeQuery(whiteLabelApi.endpoints.getWhiteLabelCompanies, {
              ...STAT_QUERY_PARAMS,
              page,
            })
          );
        }

        const remainingResults = remainingPages.length
          ? await Promise.all(remainingPages)
          : [];

        const companies = [
          ...(firstPage?.items || []),
          ...remainingResults.flatMap((result) => result?.items || []),
        ];

        const lastBuildAt = companies.reduce((latest, company) => {
          const value = company?.buildCenter?.lastBuildAt;
          if (!value) return latest;
          const nextTime = new Date(value).getTime();
          if (Number.isNaN(nextTime)) return latest;
          if (!latest) return value;
          return nextTime > new Date(latest).getTime() ? value : latest;
        }, null);

        const androidApps = companies.filter((company) => {
          const packageName = String(
            company?.appConfig?.androidPackageName || ""
          ).trim();
          const status = String(
            company?.buildCenter?.androidStatus || ""
          ).trim().toLowerCase();
          return Boolean(packageName) || (status !== "" && status !== "idle");
        }).length;

        const websiteCounts = await mapWithConcurrency(
          companies,
          6,
          async (company) => {
            try {
              const website = await executeQuery(
                whiteLabelApi.endpoints.getWhiteLabelWebsiteByCompany,
                company?._id
              );
              return website ? 1 : 0;
            } catch (websiteError) {
              if (websiteError?.status === 404) {
                return 0;
              }

              throw new Error(
                getErrorMessage(
                  websiteError,
                  "Failed to load website build metrics."
                )
              );
            }
          }
        );

        const websites = websiteCounts.reduce(
          (sum, value) => sum + Number(value || 0),
          0
        );

        const pendingBuilds = companies.reduce(
          (sum, company) => sum + Number(company?.buildCenter?.pendingBuilds || 0),
          0
        );

        if (!isMounted) return;

        setOverview({
          totalCompanies: Number(firstPage?.pagination?.totalCount || companies.length),
          androidApps,
          websites,
          lastBuildAt,
          pendingBuilds,
          companies,
        });
      } catch (loadError) {
        if (!isMounted) return;
        const message = getErrorMessage(
          loadError,
          "Unable to load build center overview."
        );
        setErrorMessage(message);
        toast.error(message);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadOverview();

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  const statCards = useMemo(
    () => [
      {
        key: "total-companies",
        label: "Total Companies",
        value: isLoading ? "Loading..." : overview.totalCompanies,
        detail: "White-label tenants visible in build preparation",
        tone: "neutral",
        icon: <ApartmentRoundedIcon fontSize="small" />,
      },
      {
        key: "android-apps",
        label: "Android Apps",
        value: isLoading ? "Loading..." : overview.androidApps,
        detail: "Companies with Android app metadata or build state",
        tone: "info",
        icon: <AndroidRoundedIcon fontSize="small" />,
      },
      {
        key: "websites",
        label: "Websites",
        value: isLoading ? "Loading..." : overview.websites,
        detail: "Companies with website configuration references",
        tone: "violet",
        icon: <PublicRoundedIcon fontSize="small" />,
      },
      {
        key: "last-build",
        label: "Last Build",
        value: isLoading ? "Loading..." : formatDateTime(overview.lastBuildAt),
        detail: "Latest company-level build timestamp currently stored",
        tone: "warning",
        icon: <ScheduleRoundedIcon fontSize="small" />,
      },
      {
        key: "pending-builds",
        label: "Pending Builds",
        value: isLoading ? "Loading..." : overview.pendingBuilds,
        detail: "Queued build count from existing company metadata",
        tone: "danger",
        icon: <PendingActionsRoundedIcon fontSize="small" />,
      },
    ],
    [isLoading, overview]
  );

  const androidStatusSummary = useMemo(() => {
    return overview.companies.reduce(
      (accumulator, company) => {
        const status = String(company?.buildCenter?.androidStatus || "idle")
          .trim()
          .toLowerCase();
        accumulator[status] = (accumulator[status] || 0) + 1;
        return accumulator;
      },
      { idle: 0, pending: 0, building: 0, failed: 0, live: 0 }
    );
  }, [overview.companies]);

  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          p: { xs: 2, md: 2.5 },
          border: `1px solid ${alpha(theme.palette.divider, 0.88)}`,
          bgcolor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.paper, 0.92)
              : alpha(theme.palette.common.white, 0.96),
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 18px 40px ${alpha("#020617", 0.28)}`
              : `0 18px 40px ${alpha("#0f172a", 0.06)}`,
        }}
      >
        <Stack
          direction={{ xs: "column", xl: "row" }}
          spacing={2}
          justifyContent="space-between"
        >
          <Box sx={{ maxWidth: 860 }}>
            <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.75 }}>
              Build Center
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              Prepare white-label delivery artifacts from the existing admin shell.
              Build orchestration remains disabled until backend build and packaging
              APIs are implemented.
            </Typography>
          </Box>

          <Chip
            icon={<ArchitectureRoundedIcon />}
            label="Management UI Only"
            color="warning"
            variant="outlined"
            sx={{ fontWeight: 800, alignSelf: { xs: "flex-start", xl: "center" } }}
          />
        </Stack>
      </Paper>

      <FlexibleStatsRow items={statCards} />

      {errorMessage ? (
        <Alert severity="error" sx={{ borderRadius: 3 }}>
          {errorMessage}
        </Alert>
      ) : null}

      {!isLoading && !overview.totalCompanies ? (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          No companies are available yet. Build Center actions will become useful
          after at least one white-label company is configured.
        </Alert>
      ) : null}

      <Stack spacing={3}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Build Operations
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Actions are intentionally disabled until backend endpoints exist.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {isLoading ? (
              <Skeleton variant="rounded" width={180} height={34} />
            ) : (
              Object.entries(androidStatusSummary).map(([status, count]) => (
                <Chip
                  key={status}
                  size="small"
                  label={`${status}: ${count}`}
                  sx={{ fontWeight: 700, textTransform: "capitalize" }}
                />
              ))
            )}
          </Stack>
        </Stack>

        <Stack spacing={2}>
          {ACTION_GROUPS.map(({ key, ...group }) => (
            <BuildPanel key={key} {...group} />
          ))}
        </Stack>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${alpha(theme.palette.divider, 0.88)}`,
          bgcolor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.paper, 0.92)
              : alpha(theme.palette.common.white, 0.96),
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 18px 40px ${alpha("#020617", 0.28)}`
              : `0 18px 40px ${alpha("#0f172a", 0.06)}`,
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 2.5 },
            py: 2.25,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.88)}`,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Recent Builds
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Build history will appear here once a backend history endpoint is available.
              </Typography>
            </Box>
            <Chip
              icon={<HistoryRoundedIcon />}
              label="Backend Not Available"
              color="warning"
              variant="outlined"
              sx={{ fontWeight: 800 }}
            />
          </Stack>
        </Box>

        <ScrollableTableContainer sx={{ maxHeight: 420 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {["Company", "Platform", "Version", "Status", "Created At", "Triggered By"].map(
                  (label) => (
                    <TableCell
                      key={label}
                      sx={{
                        fontWeight: 900,
                        whiteSpace: "nowrap",
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? alpha(theme.palette.background.default, 0.92)
                            : "#f8fafc",
                      }}
                    >
                      {label}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 4 }).map((_, index) => (
                  <TableRow key={`build-skeleton-${index}`}>
                    {Array.from({ length: 6 }).map((__, cellIndex) => (
                      <TableCell key={`build-skeleton-${index}-${cellIndex}`}>
                        <Skeleton height={24} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 5 }}>
                    <Stack spacing={1.25} alignItems="center" textAlign="center">
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 3,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                        }}
                      >
                        <HistoryRoundedIcon />
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                        No build history available yet
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 640 }}>
                        The current backend does not expose a recent builds API. Once build
                        orchestration and history tracking are implemented, this table can render
                        company, platform, version, status, created time, and trigger details.
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollableTableContainer>

        <Box
          sx={{
            px: { xs: 2, md: 2.5 },
            py: 1.75,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.88)}`,
          }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Backend dependencies still required: config generation, branding asset packaging,
            Android build triggers, website packaging, artifact download endpoints, and build history.
          </Typography>
        </Box>
      </Paper>
    </Stack>
  );
}
